import { useCallback, useMemo } from "react"

import * as turf from "@turf/turf"

import { MapWithPhotoViewer } from "@l2r-front/l2r-common"
import { useImageCollectionStateContext, useImageCollectionDispatchContext } from "@l2r-front/l2r-images"
import { ReactMapGl, useMapContext } from "@l2r-front/l2r-map"
import { RoadSearch, useIsFetchingNetwork, useNetworksStateContext } from "@l2r-front/l2r-networks"
import { PropTypes } from "@l2r-front/l2r-proptypes"
import { useIsMobileDevice } from "@l2r-front/l2r-ui"

import { useIsFetchingRoadway } from "../../../modules/roadway/hooks/useIsFetchingRoadway"
import { useIsFetchingRoadworks } from "../../../modules/roadworks/hooks/useIsFetchingRoadworks"

import { RoadMapLegend } from "../RoadMapLegend"

export function Map(props) {

    const {
        children,
        mapProps,
        ...otherProps
    } = props

    const isFetchingRoadway = useIsFetchingRoadway()
    const isFetchingRoadworks = useIsFetchingRoadworks()
    const isFetchingNetwork = useIsFetchingNetwork()
    const isFetching = isFetchingRoadway || isFetchingRoadworks || isFetchingNetwork
    const { selectedNetwork } = useNetworksStateContext()
    const [mapState, mapDispatch] = useMapContext()
    const { onOpenImageViewer, onSwitchImageLayer, isImageCollectionAvailable } = useImageCollectionDispatchContext()
    const { imageLayerDisplayed } = useImageCollectionStateContext()
    const { setMapBoundingBoxToFit } = mapDispatch
    const { error } = mapState
    const isMobile = useIsMobileDevice()

    const mapInitialViewState = useMemo(() => {
        const [minLng, minLat, maxLng, maxLat] = turf.bbox(selectedNetwork.boundingBox)
        return {
            longitude: (minLng + maxLng) / 2,
            latitude: (minLat + maxLat) / 2,
            zoom: 8,
        }
    }, [selectedNetwork])

    const onRecenterOnNetworkBoundingBox = useCallback(() => {
        const [minLng, minLat, maxLng, maxLat] = turf.bbox(selectedNetwork.boundingBox)
        setMapBoundingBoxToFit({
            minLng,
            minLat,
            maxLng,
            maxLat,
        })
    }, [selectedNetwork, setMapBoundingBoxToFit])

    const onSwitchImageLayerCallback = isImageCollectionAvailable() ? onSwitchImageLayer : undefined


    return (
        <MapWithPhotoViewer
            dragRotate={false}
            error={error}
            initialViewState={mapInitialViewState}
            isBaseMap
            isLoading={isFetching}
            imageLayerDisplayed={imageLayerDisplayed}
            topLeftElements={[<RoadSearch key="road-search" disableExpend={!isMobile} />, <RoadMapLegend key="road-map-legend" />]}
            onOpenImageViewer={onOpenImageViewer}
            onSwitchImageLayer={onSwitchImageLayerCallback}
            onRecenter={onRecenterOnNetworkBoundingBox}
            {...mapProps}
            {...otherProps}
        >
            {children}
        </MapWithPhotoViewer>
    )
}

Map.propTypes = {
    ...ReactMapGl.propTypes,
    className: PropTypes.string,
}